import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

interface IssueRepairItem {
  issueNumber: string;
  id?: string;
  name: string;
  type: string;
  issueTime: number;
  explain: string;
  userName: string;
  deviceType: string;
  tenantName: string;
  status?: 'pending' | 'processing' | 'completed' | 'rejected';
}

@Component({
  selector: 'app-issue-repair-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzCardModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzGridModule,
    NzDividerModule,
    NzBreadCrumbModule,
    NzPaginationModule,
    NzTagModule,
    NzToolTipModule,
    NzAffixModule,
    NzModalModule,
    NzSelectModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './issue-repair-list.component.html',
  styleUrls: ['./issue-repair-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IssueRepairListComponent implements OnInit {
  private readonly httpService = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  loading = false;
  pageSize = 20;
  pageIndex = 1;
  total = 0;
  listOfData: IssueRepairItem[] = [];
  searchKeywords = '';
  refreshTime = Date.now();
  // 添加到組件類中
  orderBy: string = 'issueTime';
  sortOrder: string = 'desc';
  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    const params = {
      pageIndex: this.pageIndex - 1,
      pageSize: this.pageSize,
      key: this.searchKeywords,
      sort: this.orderBy,
      orderBy: this.orderBy,
      refreshTime: this.refreshTime,
      direction: this.sortOrder
    };

    this.httpService
      .post(ConstantUtil.BASE_REPAIR_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: any) => {
          this.listOfData = response.data.items || [];
          this.total = response.data.totalCount || 0;
        },
        error: error => {
          this.message.error(this.i18n.fanyi('issue-repair.list.load-failed'));
        }
      });
  }

  search(): void {
    this.pageIndex = 1;
    this.loadData();
  }

  reset(): void {
    this.searchKeywords = '';
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  onPageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadData();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }
  // 排序變更處理
  onSortChange(orderBy: string): void {
    this.orderBy = orderBy;
    this.loadData();
  }

  // 表格排序處理
  onSortOrderChange(sortKey: string, sortOrder: string | null): void {
    this.orderBy = sortKey;
    this.sortOrder = sortOrder || 'desc';
    this.loadData();
  }

  viewDetail(item: IssueRepairItem): void {
    if (item.id) {
      this.router.navigate(['/repairs/detail', item.id]);
    } else {
      this.message.error(this.i18n.fanyi('issue-repair.list.invalid-id'));
    }
  }
}
